import { type ReactNode } from 'react'

import styles from './HeroRedesignLayout.module.css'

type HeroRedesignAdBreakpoint = 'lg' | 'xl'
type HeroRedesignAdMinHeight = 'min-h-[90px]' | 'min-h-[100px]'

export type HeroRedesignLayoutParams = Readonly<{
  adBreakpoint: HeroRedesignAdBreakpoint
  adMinHeightClass: HeroRedesignAdMinHeight
  adUnit: ReactNode
  children: ReactNode
  containerClassName?: string
  flexRowClassName?: string
  outerClassName?: string
}>

const flexRowClassNames: Record<HeroRedesignAdBreakpoint, string> = {
  lg: 'relative z-[1] mx-4 py-4 lg:flex lg:items-stretch lg:py-16',
  xl: 'relative z-[1] mx-4 py-4 lg:py-16 xl:flex xl:items-stretch',
}

const adFillClassNames: Record<HeroRedesignAdBreakpoint, string> = {
  lg: styles.adFillLg,
  xl: styles.adFillXl,
}

/*
 * No `min-h` at the ad breakpoint: the container's height must equal the search
 * form's height exactly, since the ad-fill class sizes the GPT slot and iframe
 * to fill this box. A floor here would render the ad taller than the form.
 */
const adUnitClassNames: Record<HeroRedesignAdBreakpoint, Record<HeroRedesignAdMinHeight, string>> = {
  lg: {
    'min-h-[90px]':
      'relative m-4 min-h-[90px] text-center hidden sm:block lg:m-0 lg:-ml-8 lg:w-[350px] lg:shrink-0 lg:self-stretch lg:overflow-hidden lg:rounded-tr-2xl lg:rounded-br-2xl lg:z-[-1]',
    'min-h-[100px]':
      'relative m-4 min-h-[100px] text-center hidden sm:block lg:m-0 lg:-ml-8 lg:w-[350px] lg:shrink-0 lg:self-stretch lg:overflow-hidden lg:rounded-tr-2xl lg:rounded-br-2xl lg:z-[-1]',
  },
  xl: {
    'min-h-[90px]':
      'relative m-4 min-h-[90px] text-center hidden sm:block xl:m-0 xl:-ml-8 xl:w-[350px] xl:shrink-0 xl:self-stretch xl:overflow-hidden xl:rounded-tr-2xl xl:rounded-br-2xl xl:z-[-1]',
    'min-h-[100px]':
      'relative m-4 min-h-[100px] text-center hidden sm:block xl:m-0 xl:-ml-8 xl:w-[350px] xl:shrink-0 xl:self-stretch xl:overflow-hidden xl:rounded-tr-2xl xl:rounded-br-2xl xl:z-[-1]',
  },
}

export function HeroRedesignLayout({
  adBreakpoint,
  adMinHeightClass,
  adUnit,
  children,
  containerClassName = 'mx-auto w-full max-w-[1280px]',
  flexRowClassName,
  outerClassName = 'relative',
}: HeroRedesignLayoutParams) {
  const flexRowClasses = flexRowClassName ?? flexRowClassNames[adBreakpoint]
  const adUnitClasses = `${adFillClassNames[adBreakpoint]} ${adUnitClassNames[adBreakpoint][adMinHeightClass]}`

  return (
    <div className={outerClassName}>
      <div className={containerClassName}>
        <div className={flexRowClasses}>
          <div className='w-full'>{children}</div>
          <div className={adUnitClasses} data-testid='hero-ad-unit'>
            {adUnit}
          </div>
        </div>
      </div>
    </div>
  )
}
